import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as Victory from 'victory';

const {
  VictoryPie, VictoryLabel, VictoryChart, VictoryAxis,
  VictoryBar, VictoryGroup, VictoryLegend, VictoryTooltip,
} = Victory;

function SliceWithTitle(props) {
  return (
    <g>
      {React.createElement(Victory.Slice, props)}
      <title>{props.datum?.x}</title>
    </g>
  );
}
SliceWithTitle.role = 'data';

// Wraps Victory.LineSegment to offset the y coords (compensates for Victory's
// indicator bug where centerY uses raw height/2 ignoring chart padding).
function ShiftedLineSegment({y1, y2, dy = 0, ...rest}) {
  return <Victory.LineSegment {...rest} y1={(y1 ?? 0) + dy} y2={(y2 ?? 0) + dy} />;
}

const EXPENSE_CATEGORIES = [
  'Advertising Renamed',
  'Advertising/Promotional',
  'Airfare',
  'Bad Debts',
  'Bank Charges',
  'Client entertainment event',
  'Commissions & fees',
  'Cost of Goods Sold',
  'Cost of Labor',
  'Cost of labor - COS',
  'Disposal Fees',
  'Dues & Subscriptions',
  'Dues & Subscriptions:Yuwen has <$500',
  'Equity Test',
  'Expensify Invoice Payment Fees',
  'Freight & Delivery',
  'Freight & delivery - COS',
  'Hello ',
  'Insurance',
  'Insurance - Disability',
  'Insurance - Liability',
  'Interest Expense',
  'Inventory Asset',
  'Job Materials',
  'Legal & Professional Fees',
  'Legal & Professional Fees:Repair & Maintenance',
  'Meals and Entertainment',
  'Miscellaneous',
  'New Test Expense Account',
  'Not the Expensify Credit Card Fees',
  'Not_ExpCreditCardFees',
  'OLD EXPENSIFY CARD FEED',
  'Office Expenses',
  'Opening Balance Equity',
  'Other Costs - COS',
  'Other General and Admin Expenses',
  'Penalties & Settlements',
  'Postage And Delivery',
  'Prepaid Expenses',
  "Previous Owner's Distribution",
  'PromotionalRENAMED',
  'Purchases',
  'Purchases - COS',
  'Reconciliation Discrepancies',
  'Rent or Lease',
  'René',
  'Repair & Maintenance',
  'Retained Earnings',
  'Rodrigo Test',
  'Shipping and delivery expense Shipping and delivery expenseShipping and delivery expenseShipping and delivery expenseShipping and delivery expenseShipping and delivery expenseShipping and delivery expenseShipping and delivery expense',
  'Subcontractors',
  'Subcontractors - COS',
  'Supplies',
  'Supplies & Materials - COGS',
  'Taxes & Licenses',
  'Test ABC123',
  'Testing ñ',
  'Tools',
  'Travel',
  'Travel Meals',
  'Travel Meals:Yuwen > Francois',
  'Unapplied Cash Bill Payment Expense',
  'Uncategorized Asset',
  'Uncategorized Expense',
  'Undeposited Funds',
  'Utilities',
  'Yuwen has >$500',
];

function randomAmount(min, max) {
  const logMin = Math.log(min);
  const logMax = Math.log(max);
  const value = Math.exp(logMin + Math.random() * (logMax - logMin));
  return Math.round(value / 100) * 100;
}

function generatePieData(sliceCount) {
  const picked = [...EXPENSE_CATEGORIES]
    .sort(() => Math.random() - 0.5)
    .slice(0, sliceCount - 1);

  // Step 1: pick a target total (log-distributed between $10k and $10M)
  const target = randomAmount(10000, 10000000);

  // Step 2: random weights for the non-Other slices
  const otherSliceWeights = picked.map(() => 1 + Math.random() * 10);
  const maxOtherWeight = Math.max(...otherSliceWeights);

  // Step 3: Other gets 1.15-1.6x the biggest other slice so it's always the largest
  const otherWeight = maxOtherWeight * (1.15 + Math.random() * 0.45);

  // Step 4: scale all weights so they sum to the target total
  const totalWeight = otherWeight + otherSliceWeights.reduce((s, w) => s + w, 0);
  const scale = target / totalWeight;

  const items = picked.map((name, i) => ({
    x: name,
    y: Math.round((otherSliceWeights[i] * scale) / 100) * 100,
  }));

  return [
    { x: 'Other', y: Math.round((otherWeight * scale) / 100) * 100 },
    ...items,
  ].sort((a, b) => b.y - a.y);
}

function formatPieData(data) {
  const items = data.map((d) => `    {x: ${JSON.stringify(d.x)}, y: ${d.y}}`).join(',\n');
  return `[\n${items},\n  ]`;
}

const HIDDEN_AXIS_STYLE = `style={{
      axis: {stroke: 'transparent'},
      ticks: {stroke: 'transparent'},
      grid: {stroke: 'transparent'},
      tickLabels: {fill: 'transparent'},
    }}`;

function makePreset6(data) {
  const total = data.reduce((sum, d) => sum + d.y, 0);
  return `<VictoryChart
  width={680}
  height={530}
  padding={{top: 20, bottom: 0, left: 0, right: 0}}
  domainPadding={0}
  style={{
    parent: {
      backgroundColor: '#F7F2EF',
      borderRadius: 16,
      width: '100%',
      maxWidth: 680,
    },
  }}
>
  <VictoryAxis ${HIDDEN_AXIS_STYLE} tickFormat={() => ''} />
  <VictoryAxis dependentAxis ${HIDDEN_AXIS_STYLE} tickFormat={() => ''} />
  <VictoryLabel
    x={32}
    y={40}
    text='Top categories'
    style={{ fill: '#002E22', fontSize: 17, fontWeight: 700, fontFamily: 'Expensify Neue' }}
  />
  <VictoryLabel
    x={32}
    y={62}
    text='As of: May 6, 12:49 PM PT'
    style={{ fill: '#76847E', fontSize: 11, fontWeight: 400, fontFamily: 'Expensify Neue' }}
  />
  <VictoryPie
    standalone={false}
    width={680}
    height={550}
    padding={{top: 0, bottom: 0, left: 0, right: 0}}
    innerRadius={125}
    padAngle={0.5}
    radius={145}
    labelRadius={({index}) => (index === 1 ? 215 : (index % 2 === 0 ? 215 : 170))}
    dataComponent={<SliceWithTitle />}
    labelIndicator={
      <ShiftedLineSegment
        dy={10}
        style={{ stroke: '#E6E1DA', strokeWidth: 1 }}
      />
    }
    labelIndicatorInnerOffset={15}
    labelIndicatorOuterOffset={8}
    colorScale={['#FED607', '#FF7101', '#F68DFE', '#03D47C', '#50EEF6', '#0185FF']}
    data={${formatPieData(data)}}
    labels={({datum}) => \`\${datum.x.length > 18 ? datum.x.slice(0, 17) + '…' : datum.x}\\n$\${datum.y.toLocaleString()}\`}
    labelComponent={
      <VictoryLabel
        lineHeight={[1.2, 1.4]}
        style={[
          { fill: '#002E22', fontSize: 11, fontWeight: 700, fontFamily: 'Expensify Neue' },
          { fill: '#76847E', fontSize: 11, fontWeight: 400, fontFamily: 'Expensify Neue' },
        ]}
      />
    }
    style={{
      data: { stroke: '#F7F2EF' },
    }}
  />
  <VictoryLabel
    x={340}
    y={265}
    textAnchor='middle'
    verticalAnchor='middle'
    text={"Total\\n$${total.toLocaleString()}"}
    lineHeight={[16 / 13, 1.27]}
    style={[
      { fill: '#76847E', fontSize: 13, fontFamily: 'Expensify Neue' },
      { fill: '#002E22', fontSize: 26, fontFamily: 'Expensify New Kansas' },
    ]}
  />
</VictoryChart>`;
}

function makePreset10(data) {
  const total = data.reduce((sum, d) => sum + d.y, 0);
  return `<VictoryChart
  width={680}
  height={610}
  padding={{top: 0, bottom: 0, left: 0, right: 0}}
  domainPadding={0}
  style={{
    parent: {
      backgroundColor: '#F7F2EF',
      borderRadius: 16,
      width: '100%',
      maxWidth: 680,
    },
  }}
>
  <VictoryAxis ${HIDDEN_AXIS_STYLE} tickFormat={() => ''} />
  <VictoryAxis dependentAxis ${HIDDEN_AXIS_STYLE} tickFormat={() => ''} />
  <VictoryLabel
    x={32}
    y={40}
    text='Top categories'
    style={{ fill: '#002E22', fontSize: 17, fontWeight: 700, fontFamily: 'Expensify Neue' }}
  />
  <VictoryLabel
    x={32}
    y={62}
    text='As of: May 6, 12:49 PM PT'
    style={{ fill: '#76847E', fontSize: 11, fontWeight: 400, fontFamily: 'Expensify Neue' }}
  />
  <VictoryPie
    standalone={false}
    width={680}
    height={610}
    padding={{top: 20, bottom: 0, left: 0, right: 0}}
    innerRadius={125}
    padAngle={0.5}
    radius={145}
    labelRadius={({index}) => 220 - (index % 3) * 25}
    dataComponent={<SliceWithTitle />}
    labelIndicator={
      <Victory.LineSegment
        style={{ stroke: '#E6E1DA', strokeWidth: 1 }}
      />
    }
    labelIndicatorInnerOffset={15}
    labelIndicatorOuterOffset={8}
    colorScale={[
      '#FED607', '#FF7101', '#F68DFE', '#03D47C', '#50EEF6',
      '#0185FF', '#D18000', '#BF3013', '#CF4CD9', '#008C59',
    ]}
    data={${formatPieData(data)}}
    labels={({datum}) => \`\${datum.x.length > 12 ? datum.x.slice(0, 11) + '…' : datum.x}\\n$\${datum.y.toLocaleString()}\`}
    labelComponent={
      <VictoryLabel
        lineHeight={[1.2, 1.4]}
        style={[
          { fill: '#002E22', fontSize: 11, fontWeight: 700, fontFamily: 'Expensify Neue' },
          { fill: '#76847E', fontSize: 11, fontWeight: 400, fontFamily: 'Expensify Neue' },
        ]}
      />
    }
    style={{
      data: { stroke: '#F7F2EF' },
    }}
  />
  <VictoryLabel
    x={340}
    y={291}
    textAnchor='middle'
    verticalAnchor='middle'
    text={"Total\\n$${total.toLocaleString()}"}
    lineHeight={[16 / 13, 1.27]}
    style={[
      { fill: '#76847E', fontSize: 13, fontFamily: 'Expensify Neue' },
      { fill: '#002E22', fontSize: 26, fontFamily: 'Expensify New Kansas' },
    ]}
  />
</VictoryChart>`;
}

const PRESET_VERTICAL = `<VictoryChart
  domain={{x: [0.5, 12.5], y: [0, 2000]}}
  domainPadding={{x: 18, y: 0}}
  height={430}
  width={680}
  padding={{top: 126, bottom: 108, left: 96, right: 32}}
  style={{
    parent: {
      backgroundColor: '#F7F2EF',
      borderRadius: 16,
      width: '100%',
      maxWidth: 680,
    },
  }}
>
  <VictoryLabel
    x={32}
    y={40}
    text='Monthly spend'
    style={{
      fill: '#002E22',
      fontSize: 17,
      fontWeight: 700,
      fontFamily: 'Expensify Neue',
    }}
  />

  <VictoryLabel
    x={32}
    y={62}
    text='As of: May 6, 12:49 PM PT'
    style={{
      fill: '#73857E',
      fontSize: 11,
      fontWeight: 400,
      fontFamily: 'Expensify Neue',
    }}
  />

  <VictoryAxis
    tickValues={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
    tickFormat={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']}
    style={{
      axis: {stroke: '#E6E1DA', strokeWidth: 1},
      ticks: {stroke: 'transparent'},
      tickLabels: {
        fill: '#73857E',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'Expensify Neue',
        padding: 16,
      },
    }}
  />

  <VictoryAxis
    dependentAxis
    tickValues={[0, 500, 1000, 1500, 2000]}
    tickFormat={['$0', '$500', '$1,000', '$1,500', '$2,000']}
    style={{
      axis: {stroke: 'transparent'},
      ticks: {stroke: 'transparent'},
      grid: {stroke: '#E6E1DA', strokeWidth: 1},
      tickLabels: {
        fill: '#73857E',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'Expensify Neue',
        padding: 28,
      },
    }}
  />

  <VictoryBar
    barWidth={11}
    cornerRadius={{top: 6, bottom: 6}}
    style={{data: {fill: '#1E90F2'}}}
    data={[
      {x: 1.13, y: 1080},
      {x: 2.13, y: 1225},
      {x: 3.13, y: 1375},
      {x: 4.13, y: 1600},
      {x: 5.13, y: 1630},
      {x: 6.13, y: 1725},
      {x: 7.13, y: 1400},
      {x: 8.13, y: 1685},
      {x: 9.13, y: 1725},
      {x: 10.13, y: 1800},
      {x: 11.13, y: 1800},
      {x: 12.13, y: 1800},
    ]}
    labels={[
      'Jan 2025: $1,080',
      'Feb 2025: $1,225',
      'Mar 2025: $1,375',
      'Apr 2025: $1,600',
      'May 2025: $1,630',
      'Jun 2025: $1,725',
      'Jul 2025: $1,400',
      'Aug 2025: $1,685',
      'Sep 2025: $1,725',
      'Oct 2025: $1,800',
      'Nov 2025: $1,800',
      'Dec 2025: $1,800',
    ]}
    labelComponent={
      <VictoryTooltip
        flyoutStyle={{fill: '#002E22', stroke: 'transparent'}}
        style={{fill: '#E7ECE9', fontSize: 11, fontFamily: 'Expensify Neue'}}
        cornerRadius={4}
        pointerLength={4}
        pointerWidth={12}
        flyoutPadding={{top: 4, bottom: 4, left: 8, right: 8}}
      />
    }
  />

  <VictoryBar
    barWidth={11}
    cornerRadius={{top: 6, bottom: 6}}
    style={{data: {fill: '#13C96B'}}}
    data={[
      {x: 0.87, y: 1200},
      {x: 1.87, y: 1320},
      {x: 2.87, y: 1570},
    ]}
    labels={['Jan 2026: $1,200', 'Feb 2026: $1,320', 'Mar 2026: $1,570']}
    labelComponent={
      <VictoryTooltip
        flyoutStyle={{fill: '#002E22', stroke: 'transparent'}}
        style={{fill: '#E7ECE9', fontSize: 11, fontFamily: 'Expensify Neue'}}
        cornerRadius={4}
        pointerLength={4}
        pointerWidth={12}
        flyoutPadding={{top: 4, bottom: 4, left: 8, right: 8}}
      />
    }
  />

  <VictoryBar
    barWidth={11}
    cornerRadius={{top: 6, bottom: 6}}
    style={{data: {fill: '#FF6A00'}}}
    data={[{x: 3.87, y: 1820}]}
    labels={['Apr 2026: $1,820']}
    labelComponent={
      <VictoryTooltip
        flyoutStyle={{fill: '#002E22', stroke: 'transparent'}}
        style={{fill: '#E7ECE9', fontSize: 11, fontFamily: 'Expensify Neue'}}
        cornerRadius={4}
        pointerLength={4}
        pointerWidth={12}
        flyoutPadding={{top: 4, bottom: 4, left: 8, right: 8}}
      />
    }
  />

  <VictoryLegend
    x={150}
    y={378}
    orientation='horizontal'
    gutter={34}
    symbolSpacer={10}
    style={{
      labels: {
        fill: '#002E22',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'Expensify Neue',
      },
    }}
    data={[
      {name: '2026 spend', symbol: {type: 'circle', fill: '#13C96B', size: 6}},
      {name: '2025 spend', symbol: {type: 'circle', fill: '#1E90F2', size: 6}},
      {name: 'Last month', symbol: {type: 'circle', fill: '#FF6A00', size: 6}},
    ]}
  />
</VictoryChart>`;

const PRESET_HORIZONTAL = `<VictoryChart
  horizontal
  domain={{y: [0, 18000]}}
  domainPadding={{x: 44, y: 16}}
  height={464}
  width={680}
  padding={{top: 92, bottom: 84, left: 150, right: 32}}
  style={{
    parent: {
      backgroundColor: '#F7F2EF',
      borderRadius: 16,
      width: '100%',
      maxWidth: 680,
    },
  }}
  categories={{
    x: ['Ethan Brooks', 'Sofia Ramirez', 'Michelina Della Donna', 'Priya Patel', 'Alex Chen'],
  }}
>
  <VictoryLabel
    x={32}
    y={40}
    text='Top employees by spend'
    style={{
      fill: '#002E22',
      fontSize: 17,
      fontWeight: 700,
      fontFamily: 'Expensify Neue',
    }}
  />

  <VictoryLabel
    x={32}
    y={62}
    text='As of: May 6, 12:49 PM PT'
    style={{
      fill: '#73857E',
      fontSize: 11,
      fontWeight: 400,
      fontFamily: 'Expensify Neue',
    }}
  />

  <VictoryAxis
    tickFormat={(name) => (name.length > 16 ? name.slice(0, 15) + '…' : name)}
    style={{
      axis: {stroke: 'transparent'},
      ticks: {stroke: 'transparent'},
      grid: {stroke: 'transparent'},
      tickLabels: {
        fill: '#73857E',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'Expensify Neue',
        padding: 24,
      },
    }}
  />

  <VictoryAxis
    dependentAxis
    tickValues={[0, 3000, 6000, 9000, 12000, 15000, 18000]}
    tickFormat={['$0', '$3,000', '$6,000', '$9,000', '$12,000', '$15,000', '$18,000']}
    style={{
      axis: {stroke: 'transparent'},
      ticks: {stroke: 'transparent'},
      grid: {stroke: '#E6E1DA', strokeWidth: 1},
      tickLabels: {
        fill: '#73857E',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'Expensify Neue',
        padding: 8,
      },
    }}
  />

  <VictoryGroup horizontal offset={18}>
    <VictoryBar
      barWidth={16}
      cornerRadius={{top: 8, bottom: 0}}
      style={{data: {fill: '#13C96B'}}}
      data={[
        {x: 'Ethan Brooks', y: 9450},
        {x: 'Sofia Ramirez', y: 10500},
        {x: 'Michelina Della Donna', y: 11900},
        {x: 'Priya Patel', y: 13400},
        {x: 'Alex Chen', y: 14300},
      ]}
      labels={[
        'Ethan Brooks, this month: $9,450',
        'Sofia Ramirez, this month: $10,500',
        'Michelina Della Donna, this month: $11,900',
        'Priya Patel, this month: $13,400',
        'Alex Chen, this month: $14,300',
      ]}
      labelComponent={
        <VictoryTooltip
          flyoutStyle={{fill: '#002E22', stroke: 'transparent'}}
          style={{fill: '#E7ECE9', fontSize: 11, fontFamily: 'Expensify Neue'}}
          cornerRadius={4}
          pointerLength={4}
          pointerWidth={12}
          flyoutPadding={{top: 4, bottom: 4, left: 8, right: 8}}
        />
      }
    />

    <VictoryBar
      barWidth={16}
      cornerRadius={{top: 8, bottom: 0}}
      style={{data: {fill: '#1E90F2'}}}
      data={[
        {x: 'Ethan Brooks', y: 8800},
        {x: 'Sofia Ramirez', y: 10250},
        {x: 'Michelina Della Donna', y: 11250},
        {x: 'Priya Patel', y: 13900},
        {x: 'Alex Chen', y: 12500},
      ]}
      labels={[
        'Ethan Brooks, last month: $8,800',
        'Sofia Ramirez, last month: $10,250',
        'Michelina Della Donna, last month: $11,250',
        'Priya Patel, last month: $13,900',
        'Alex Chen, last month: $12,500',
      ]}
      labelComponent={
        <VictoryTooltip
          flyoutStyle={{fill: '#002E22', stroke: 'transparent'}}
          style={{fill: '#E7ECE9', fontSize: 11, fontFamily: 'Expensify Neue'}}
          cornerRadius={4}
          pointerLength={4}
          pointerWidth={12}
          flyoutPadding={{top: 4, bottom: 4, left: 8, right: 8}}
        />
      }
    />
  </VictoryGroup>

  <VictoryLegend
    x={250}
    y={416}
    orientation='horizontal'
    gutter={42}
    symbolSpacer={10}
    style={{
      labels: {
        fill: '#002E22',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'Expensify Neue',
      },
    }}
    data={[
      {name: 'This month', symbol: {type: 'circle', fill: '#13C96B', size: 6}},
      {name: 'Last month', symbol: {type: 'circle', fill: '#1E90F2', size: 6}},
    ]}
  />
</VictoryChart>`;

function buildPresets(pie6Data, pie10Data) {
  return {
    '6': makePreset6(pie6Data),
    '10': makePreset10(pie10Data),
    vertical: PRESET_VERTICAL,
    horizontal: PRESET_HORIZONTAL,
  };
}

const VARIANT_LABELS = {
  '6': 'Top categories (6)',
  '10': 'Top categories (10)',
  vertical: 'Monthly spend',
  horizontal: 'Top employees by spend',
};

function Preview({ code }) {
  try {
    const transformed = window.Babel.transform(
      `const __el = (${code});`,
      { presets: ['react'] }
    ).code.replace('const __el =', 'return ');

    // eslint-disable-next-line no-new-func
    const fn = new Function(
      'React', 'VictoryPie', 'VictoryLabel', 'VictoryChart', 'VictoryAxis',
      'VictoryBar', 'VictoryGroup', 'VictoryLegend', 'VictoryTooltip',
      'SliceWithTitle', 'ShiftedLineSegment', 'Victory',
      transformed
    );
    const el = fn(
      React, VictoryPie, VictoryLabel, VictoryChart, VictoryAxis,
      VictoryBar, VictoryGroup, VictoryLegend, VictoryTooltip,
      SliceWithTitle, ShiftedLineSegment, Victory,
    );
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {el}
      </div>
    );
  } catch (e) {
    return (
      <div style={{
        padding: '1rem',
        background: '#fff0f0',
        borderRadius: 8,
        fontFamily: 'monospace',
        fontSize: 12,
        color: '#c00',
        whiteSpace: 'pre-wrap',
      }}>
        {e.message}
      </div>
    );
  }
}

export default function App() {
  const [variant, setVariant] = useState('6');
  const [pie6Data, setPie6Data] = useState(() => generatePieData(6));
  const [pie10Data, setPie10Data] = useState(() => generatePieData(10));
  const presets = buildPresets(pie6Data, pie10Data);
  const [code, setCode] = useState(() => makePreset6(pie6Data));
  const [liveCode, setLiveCode] = useState(() => makePreset6(pie6Data));
  const [copied, setCopied] = useState(false);
  const [editorPct, setEditorPct] = useState(50);
  const taRef = useRef(null);
  const splitRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setEditorPct(Math.min(85, Math.max(15, pct)));
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = () => {
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const run = useCallback(() => setLiveCode(code), [code]);

  const switchVariant = (v) => {
    setVariant(v);
    setCode(presets[v]);
    setLiveCode(presets[v]);
  };

  const randomize = () => {
    const new6 = generatePieData(6);
    const new10 = generatePieData(10);
    setPie6Data(new6);
    setPie10Data(new10);
    if (variant === '6') {
      const next = makePreset6(new6);
      setCode(next);
      setLiveCode(next);
    } else if (variant === '10') {
      const next = makePreset10(new10);
      setCode(next);
      setLiveCode(next);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      run();
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = taRef.current;
      const s = ta.selectionStart, end = ta.selectionEnd;
      const newVal = ta.value.substring(0, s) + '  ' + ta.value.substring(end);
      setCode(newVal);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const reset = () => {
    setCode(presets[variant]);
    setLiveCode(presets[variant]);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', background: '#fff',
        borderBottom: '1px solid #e5e5e5',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
            Virtual CFO Charts
          </span>
          <div style={{ display: 'inline-flex', background: '#f0f0f0', borderRadius: 7, padding: 2 }}>
            {Object.keys(presets).map(key => {
              const active = variant === key;
              return (
                <button
                  key={key}
                  onClick={() => switchVariant(key)}
                  style={{
                    padding: '4px 12px',
                    border: 'none',
                    borderRadius: 5,
                    background: active ? '#fff' : 'transparent',
                    color: '#333',
                    fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                  }}
                >
                  {VARIANT_LABELS[key]}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={randomize}
            disabled={variant !== '6' && variant !== '10'}
            style={{ ...btnStyle, opacity: (variant === '6' || variant === '10') ? 1 : 0.4 }}
            title="Randomize pie data"
          >
            🎲 Randomize
          </button>
          <button onClick={reset} style={btnStyle}>Reset</button>
          <button onClick={copy} style={btnStyle}>{copied ? '✓ Copied' : 'Copy code'}</button>
          <button onClick={run} style={{ ...btnStyle, background: '#002E22', color: '#fff', borderColor: '#002E22' }}>
            ▶ Run <kbd style={{ fontSize: 10, opacity: 0.7, marginLeft: 4 }}>⌘↵</kbd>
          </button>
        </div>
      </div>

      {/* Split pane */}
      <div ref={splitRef} style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Editor */}
        <div style={{ width: `${editorPct}%`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '6px 14px', background: '#fafafa', borderBottom: '1px solid #e5e5e5', fontSize: 11, color: '#888' }}>
            JSX — edit and run
          </div>
          <textarea
            ref={taRef}
            value={code}
            onChange={e => setCode(e.target.value)}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            style={{
              flex: 1,
              fontFamily: "'Fira Code', 'Cascadia Code', 'Menlo', monospace",
              fontSize: 13,
              lineHeight: 1.7,
              padding: '14px 16px',
              border: 'none',
              outline: 'none',
              resize: 'none',
              background: '#1e1e1e',
              color: '#d4d4d4',
            }}
          />
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={startDrag}
          style={{
            width: 6,
            cursor: 'col-resize',
            background: '#e5e5e5',
            flexShrink: 0,
            position: 'relative',
          }}
          title="Drag to resize"
        >
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 2, height: 32, borderRadius: 2, background: '#bbb',
          }} />
        </div>

        {/* Preview */}
        <div style={{ width: `${100 - editorPct}%`, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <div style={{ padding: '6px 14px', background: '#fafafa', borderBottom: '1px solid #e5e5e5', fontSize: 11, color: '#888' }}>
            Preview
          </div>
          <div style={{ flex: 1, padding: '1.5rem', overflow: 'auto' }}>
            <Preview code={liveCode} />
          </div>
        </div>

      </div>
    </div>
  );
}

const btnStyle = {
  fontSize: 12,
  padding: '5px 12px',
  borderRadius: 6,
  border: '1px solid #ddd',
  background: 'transparent',
  color: '#333',
  cursor: 'pointer',
};
